import React, { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { emptyPage, Page } from "@/model";
import Button from "@/component/Button";
import { PageFirstIcon, PageLastIcon, PageNextIcon, PagePreviousIcon } from "@/assets/svgs/icons";


type PaginationBtnType = "first" | "prev" | "next" | "last"

const ARROW_BTN_CLASSNAME = "size-6 rounded-[6px] text-black-300 hover:text-black-500 focus:bg-black-300 focus:text-white disabled:text-black-200";

interface PaginationBarProps {
    className?: string;
    pageData?: Page<any>;
    onClickPage: (page: number) => void | Promise<void>;
}

const PaginationBar: React.FC<PaginationBarProps> = (props) => {
    const { className: propsClassName, pageData = emptyPage(), onClickPage } = props;
    const { page, size, total_pages, total, content } = pageData;

    const intl = useIntl();

    const inputRef = useRef<HTMLInputElement>(null);

    const onClickBtn = useCallback(async (e: React.MouseEvent, type: PaginationBtnType) => {
        e.preventDefault();

        let toPage: number;
        switch (type) {
            case "first":
                toPage = 0;
                break;
            case "prev":
                toPage = page - 1;
                break;
            case "next":
                toPage = page + 1;
                break;
            case "last":
                toPage = total_pages - 1;
                break;
        }
        await onClickPage(toPage);
    }, [onClickPage, page, total_pages]);

    const handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!inputRef.current) {
            return;
        }
        const parsedValue = parseInt(inputRef.current.value);
        if (parsedValue < 1){
            inputRef.current.value = "1";
            return;
        } 
        if (parsedValue > total_pages){
            inputRef.current.value = total_pages.toString();
            return;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>)=> {
        if(e.key === "Enter" && inputRef.current?.value){
            const parsedValue = parseInt(inputRef.current.value);
            if (isNaN(parsedValue) || parsedValue < 1 || parsedValue > total_pages) {
                return;
            }
            onClickPage(parsedValue -1);
        }
    };

    useEffect(() => {
        if (!inputRef.current) {
            return;
        }
        inputRef.current.value = (page + 1).toString();
    }, [page]);

    return (
        <div className={classNames("w-full flex justify-between items-center", propsClassName ?? "")}>
            <div className="table-count-info">
                {intl.formatMessage({ id: "i00024" }, {
                    total: total,
                    current: page * size + (content?.length || 0)
                })}
            </div>

            <div className="flex items-center justify-center">
                <Button className={ARROW_BTN_CLASSNAME}
                        disabled={pageData.is_first}
                        onClick={e => onClickBtn(e, "first")}
                >
                    <PageFirstIcon className="size-full" />
                </Button>
                <Button className={ARROW_BTN_CLASSNAME}
                        disabled={!pageData.has_previous}
                        onClick={e => onClickBtn(e, "prev")}
                >
                    <PagePreviousIcon className="size-full" />
                </Button>

                <div className="mx-3 flex items-center gap-2 text-black-500 font-[500] text-[12px] leading-[1.4]">
                    <input ref={inputRef}
                           type="number"
                           min={1}
                           max={total_pages}
                           className="input-number-webkit"
                           onKeyDown={handleKeyDown}
                           onChange={handlePageChange}
                    />
                    {`/ ${total_pages || 1} ${intl.formatMessage({ id: "i00025" })}`}
                </div>

                <Button className={ARROW_BTN_CLASSNAME}
                        disabled={!pageData.has_next}
                        onClick={e => onClickBtn(e, "next")}
                >
                    <PageNextIcon className="size-full" />
                </Button>
                <Button className={ARROW_BTN_CLASSNAME}
                        disabled={pageData.is_last}
                        onClick={e => onClickBtn(e, "last")}
                >
                    <PageLastIcon className="size-full" />
                </Button>
            </div>
        </div>
    );
};

export default PaginationBar;